import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StateEntityGroupsService } from '../../state-entity-groups.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-state-group-add',
    templateUrl: './state-group-add.component.html',
    styleUrls: ['./state-group-add.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class StateGroupAddComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  modalTitle = "Add Group Details";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  // Data properties
  filterParams: any = {};
  baseUrl: string = '';

  // Group data
  newGroups: any[] = [];

  // Global services
  USER_SETTINGS: any;
  GENERAL_CONFIG: any;
  globalService: any;

  constructor(
    private stateEntityService: StateEntityGroupsService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.baseUrl = this.GENERAL_CONFIG.base_url || '';
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.globalService = {
      ...header,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      filterParams: this.filterParams,
    };
    this.cdr.markForCheck();
  }

  init() {
    try {
      // Initialize component - no data loading needed for create
      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error in state group add init:', e);
    }
  }

  addGroup() {
    const tempModelObject = {
      "CONSOL_TYPE": "SINGLE",
      "CREATED_BY": this.USER_SETTINGS?.user?.sso_id || "",
      "CREATED_ON": "",
      "FILER_TAX_YEAR_BEGIN": "",
      "FILER_TAX_YEAR_END": "",
      "GROUP_FLAG": "STATE",
      "GROUP_NAME": "",
      "GROUP_OBJ_KEY": "",
      "GROUP_TYPE": "REG",
      "LOCK_Y_N": "N",
      "MEF_GROUP_KEY": "",
      "PARENT_KEY": "",
      "SCENARIO": "",
      "STATE": "",
      "TAX_YEAR": "",
      "UPDATED_BY": this.USER_SETTINGS?.user?.sso_id || "",
      "UPDATED_ON": ""
    };
    this.newGroups.push(tempModelObject);
    this.cdr.detectChanges();
  }

  removeGroup(index: number) {
    if (this.newGroups.length > 0) {
      this.newGroups.splice(index, 1);
      this.cdr.detectChanges();
    }
  }

  validation(): boolean {
    if (this.newGroups.length < 1) {
      return false;
    }

    for (let i = 0; i < this.newGroups.length; i++) {
      if (!this.newGroups[i].GROUP_NAME || this.newGroups[i].GROUP_NAME.trim() === "") {
        return false;
      }
    }

    return true;
  }

  save() {
    if (!this.validation()) {
      this._snackBar.open('Group name can not be empty.', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;

    this.stateEntityService.saveStateGroup(
      this.baseUrl,
      this.newGroups,
      this.globalService,
      this.filterParams,
      this.USER_SETTINGS
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.callSuccess == "1" && response.errorMessage == "null") {
          this._snackBar.open('Groups created successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          // Emit refresh event
          this.requestGridUpdate.emit('refresh');
        } else {
          if (response.errorMessage && response.errorMessage !== 'null') {
            this._snackBar.open(response.errorMessage, 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          } else {
            this._snackBar.open('Error occurred in creating the groups. If this continues please contact support', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        console.error('Error creating state groups:', error);
        this._snackBar.open('Error occurred while creating groups. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
